// src/components/AVVProcess/Testi.jsx
import React, { useState, useMemo } from "react";
import {
  Modal,
  Button,
  Card,
  Tag,
  Row,
  Col,
  Tooltip,
  Empty,
  Typography,
} from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import UPDATES_RECEIVED from "./data.json";
import CustomCard from "../../blocks/Card/Card";
import { DemoImage } from "./DemoImage";

const images = UPDATES_RECEIVED[0].images;

const { Title } = Typography;

const RoomClassification = () => {
  const [filteredImages, setFilteredImages] = useState(images);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(null);

  const roomTypes = useMemo(() => {
    const types = images.map(
      (image) => image.models.room_classification.results[0]?.class
    );
    // Remove duplicates and handle potential null/undefined values
    const uniqueRoomTypes = Array.from(new Set(types.filter(Boolean)));
    return ["All", ...uniqueRoomTypes];
  }, []);

  const handleFilterChange = (roomType) => {
    if (roomType === "All") {
      setFilteredImages(images);
    } else {
      const filtered = images.filter((img) =>
        img.models.room_classification.results[0]?.class.includes(roomType)
      );
      setFilteredImages(filtered.length ? filtered : []);
    }
  };

  const handleImageSelect = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const handleNext = () => {
    const nextIndex = (currentIndex + 1) % filteredImages.length;
    setCurrentIndex(nextIndex);
    setSelectedImage(filteredImages[nextIndex]);
  };

  const handlePrevious = () => {
    const prevIndex =
      (currentIndex - 1 + filteredImages.length) % filteredImages.length;
    setCurrentIndex(prevIndex);
    setSelectedImage(filteredImages[prevIndex]);
  };

  return (
    <CustomCard title={<Title level={3}>Room Classification</Title>}>
      <div style={{ marginBottom: 16 }}>
        {roomTypes.map((room) => (
          <Tag
            key={room}
            onClick={() => handleFilterChange(room)}
            color="blue"
            style={{ cursor: "pointer" }}
          >
            {room}
          </Tag>
        ))}
      </div>

      <Row gutter={[16, 16]}>
        {filteredImages.length > 0 ? (
          filteredImages.map((image, index) => (
            <Col xs={24} sm={12} md={8} lg={6} key={image._id}>
              <Tooltip title="Click to view details">
                <Card
                  hoverable
                  cover={
                    <DemoImage
                      src={image.url}
                      alt="Room"
                      style={{
                        height: "210px",
                        width: "100%",
                        objectFit: "cover",
                      }}
                    />
                  }
                  onClick={() => handleImageSelect(image, index)}
                  style={{ width: "100%", height: 300 }}
                >
                  <Card.Meta
                    title={`Score: ${image.models.quality_score.results[0]?.score.toFixed(
                      2
                    )}`}
                    description={`Room: ${image.models.room_classification.results[0]?.class}`}
                  />
                </Card>
              </Tooltip>
            </Col>
          ))
        ) : (
          <Col span={24}>
            <Empty description="No images available for this room type." />
          </Col>
        )}
      </Row>

      <Modal
        visible={!!selectedImage}
        footer={null}
        onCancel={() => setSelectedImage(null)}
        width={800}
      >
        {selectedImage && (
          <div style={{ textAlign: "center" }}>
            <Button
              type="link"
              icon={<LeftOutlined />}
              onClick={handlePrevious}
              disabled={filteredImages.length <= 1}
            />
            <DemoImage
              src={selectedImage.url}
              alt="Room Detail"
              style={{ width: "100%", maxHeight: 500, objectFit: "contain" }}
            />
            <Button
              type="link"
              icon={<RightOutlined />}
              onClick={handleNext}
              disabled={filteredImages.length <= 1}
            />
            <p>{`Room: ${selectedImage?.models.room_classification.results[0]?.class}`}</p>
            <p>{`Quality Score: ${selectedImage?.models.quality_score.results[0]?.score.toFixed(
              2
            )}`}</p>
            <p>{`Condition Score: ${selectedImage?.models.condition_score.results[0]?.score.toFixed(
              2
            )}`}</p>
            <Button onClick={() => setSelectedImage(null)}>Close</Button>
          </div>
        )}
      </Modal>
    </CustomCard>
  );
};

export default RoomClassification;
